import { LazyScrolling, LazyScrollingItem, LoadingPlaceholder } from '@components-deprecated'

import { Color, Icon, Link } from '@billy/design-system'
import { css } from '@emotion/core'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { QueryParamKeys } from '../../../enums/queryParamKeys'
import { useQueryParamsStorage } from '../../../hooks/useQueryParamsStorage'
import { SpecificState } from '../../../types/reduxSaga-deprecated'
import { reactClass } from '../../../utils'
import { bankLinesAndTransactionsRequested, filtersChanged } from '../action-creators'
import { BankReconcileQueryParams, ExtendedBankLineMatch } from '../types'
import { MatchListRow } from './MatchListRow'
import * as Styled from './styles'

export type MatchListRowContext = {
  bulkSelectionMode: boolean
  isDeleteMode: boolean
  onChangeSelectedIds: (selectedIds: string[]) => void
  selectedIds: string[]
}

type MatchListProps = MatchListRowContext

const LoadingPlaceholders = () => {
  return (
    <>
      {new Array(7).fill(true).map((_, index) => (
        <Styled.MatchListGrid key={index}>
          <LoadingPlaceholder />
          <LoadingPlaceholder />
        </Styled.MatchListGrid>
      ))}
    </>
  )
}

export const MatchList = ({ bulkSelectionMode, isDeleteMode, selectedIds, onChangeSelectedIds }: MatchListProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { setQueryParams } = useQueryParamsStorage<BankReconcileQueryParams>(QueryParamKeys.MatchList)

  const isBalanceBarOpen = useSelector((state: SpecificState) => state.bankReconciliation.isBalanceBarOpen)
  const bankLineMatches = useSelector((state: SpecificState) => state.bankReconciliation.bankLineMatches)
  const bankLinesLoading = useSelector((state: SpecificState) => state.bankReconciliation.bankLinesLoading)
  const bankLinesSortProperty = useSelector(
    (state: SpecificState) => state.bankReconciliation.filters.bankLinesSortProperty,
  )
  const bankLinesSortDirection = useSelector(
    (state: SpecificState) => state.bankReconciliation.filters.bankLinesSortDirection,
  )

  const requestRows = useCallback(() => {
    dispatch(bankLinesAndTransactionsRequested(true))
  }, [dispatch])

  const createRenderMatchFunction = useCallback((contextProps: MatchListRowContext) => {
    return (match: LazyScrollingItem) => (
      <MatchListRow key={match.id} match={match as ExtendedBankLineMatch} context={contextProps} />
    )
  }, [])

  const handleSort = useCallback(
    (title: 'entryDate' | 'sideAmount') => {
      const direction = bankLinesSortProperty === title ? bankLinesSortDirection * -1 : 1

      setQueryParams({ bankLinesSortProperty: title, bankLinesSortDirection: String(direction) }, '', false)

      dispatch(
        filtersChanged({
          bankLinesSortProperty: title,
          bankLinesSortDirection: direction,
        }),
      )
    },
    [dispatch, setQueryParams, bankLinesSortProperty, bankLinesSortDirection],
  )

  return (
    <div className={reactClass('match-list-container')}>
      <Styled.MatchListGrid>
        <Styled.TableHeaderWrapper className={reactClass('list-heading')}>
          <Link onClick={() => handleSort('entryDate')}>
            {t('bankreconciliation.matchgroups.paymentdate')}
            {bankLinesSortProperty === 'entryDate' &&
              (bankLinesSortDirection === -1 ? (
                <Icon icon="chevronUp" color={Color.Lavender500} />
              ) : (
                <Icon icon="chevronDown" color={Color.Lavender500} />
              ))}
          </Link>

          <Link
            disabled
            css={css`
              flex: 1;
              margin-left: 8px;
            `}
          >
            {t('bankreconciliation.matchgroups.description')}
          </Link>

          <Link onClick={() => handleSort('sideAmount')}>
            {t('bankreconciliation.matchgroups.amount')}
            {bankLinesSortProperty === 'sideAmount' &&
              (bankLinesSortDirection === -1 ? (
                <Icon icon="chevronUp" color={Color.Lavender500} />
              ) : (
                <Icon icon="chevronDown" color={Color.Lavender500} />
              ))}
          </Link>
        </Styled.TableHeaderWrapper>
      </Styled.MatchListGrid>
      <Styled.ScrollableList className={reactClass('match-list')} isBalanceBarOpen={isBalanceBarOpen}>
        {bankLinesLoading ? (
          <LoadingPlaceholders />
        ) : (
          <LazyScrolling
            items={bankLineMatches}
            render={createRenderMatchFunction({ bulkSelectionMode, isDeleteMode, onChangeSelectedIds, selectedIds })}
            request={requestRows}
          />
        )}
      </Styled.ScrollableList>
    </div>
  )
}
